import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import PageContainer from '../components/layout/PageContainer';
import Button from '../components/ui/Button';
import { Plus, MapPin, Copy, Trash2, Edit, X, Check, Image, Link as LinkIcon, Search, Plane } from 'lucide-react';

// Image Upload Modal Component
function ImageModal({ itinerary, onClose, onSave }) {
  const [imageUrl, setImageUrl] = useState(itinerary.thumbnail_url || '');
  const [searchQuery, setSearchQuery] = useState(itinerary.destination || '');
  const [unsplashResults, setUnsplashResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('search'); // search, url

  // Curated travel photos from Unsplash (images.unsplash.com direct links)
  const TRAVEL_PHOTOS = [
    'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=80', // lake + mountains
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80', // tropical beach
    'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80', // mountain hiking
    'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=800&q=80', // travel road
    'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800&q=80', // boats harbor
    'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80', // valley landscape
    'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80', // lake panorama
    'https://images.unsplash.com/photo-1528127269322-539801943592?w=800&q=80', // Vietnam boats
    'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=800&q=80', // Venice canal
    'https://images.unsplash.com/photo-1504598318550-17eba1008a68?w=800&q=80', // desert camels
    'https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=800&q=80', // pool party
    'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=800&q=80', // ocean relaxation
  ];

  const searchUnsplash = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      // Shuffle and pick 6 photos from the curated set
      const shuffled = [...TRAVEL_PHOTOS].sort(() => Math.random() - 0.5);
      setUnsplashResults(shuffled.slice(0, 6));
    } catch (error) {
      console.error('Error searching images:', error);
    }
    setSearching(false);
  };

  useEffect(() => {
    if (activeTab === 'search' && searchQuery) {
      const timer = setTimeout(searchUnsplash, 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, activeTab]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(itinerary.id, imageUrl);
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-elevated">
        <div className="p-6 border-b border-platinum-100 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Add Cover Image</h2>
          <button onClick={onClose} className="p-2 hover:bg-platinum-100 rounded-xl transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-platinum-100">
          <button
            onClick={() => setActiveTab('search')}
            className={`flex-1 px-6 py-3.5 text-sm font-medium transition-colors ${
              activeTab === 'search' ? 'border-b-2 border-coral-400 text-coral-500' : 'text-platinum-600 hover:text-charcoal-400'
            }`}
          >
            <Search size={15} className="inline mr-2" />
            Search Photos
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`flex-1 px-6 py-3.5 text-sm font-medium transition-colors ${
              activeTab === 'url' ? 'border-b-2 border-coral-400 text-coral-500' : 'text-platinum-600 hover:text-charcoal-400'
            }`}
          >
            <LinkIcon size={15} className="inline mr-2" />
            Paste URL
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {activeTab === 'search' && (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-platinum-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for images..."
                  className="w-full pl-10 pr-4 py-3 border border-platinum-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-200 focus:border-coral-300 transition-all"
                />
              </div>

              {searching ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-400 mx-auto"></div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {unsplashResults.map((url, index) => (
                    <button
                      key={index}
                      onClick={() => setImageUrl(url)}
                      className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                        imageUrl === url ? 'border-coral-400 ring-2 ring-coral-200' : 'border-transparent'
                      }`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {imageUrl === url && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-coral-400 rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'url' && (
            <div>
              <label className="block text-sm font-medium text-charcoal-500 mb-2">
                Image URL
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-3 border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400"
              />
              {imageUrl && (
                <div className="mt-4 rounded-lg overflow-hidden border border-platinum-200">
                  <img src={imageUrl} alt="Preview" className="w-full h-40 object-cover" />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-platinum-200 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !imageUrl}>
            {saving ? 'Saving...' : 'Save Image'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const CARD_GRADIENTS = [
  'from-coral-300 to-coral-500',
  'from-columbia-400 to-columbia-700',
  'from-naples-300 to-coral-400',
  'from-charcoal-400 to-charcoal-600',
  'from-coral-200 to-columbia-500',
  'from-naples-400 to-columbia-600',
];

function TravelDesignerDashboard() {
  const [savedItineraries, setSavedItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingImage, setEditingImage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchUserAndData();
  }, []);

  const fetchUserAndData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await fetchSavedItineraries(user.id);
    }
    setLoading(false);
  };

  const fetchSavedItineraries = async (userId) => {
    const { data, error } = await supabase
      .from('itineraries')
      .select('id, title, destination, trip_length, thumbnail_url')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching itineraries:', error);
    } else {
      setSavedItineraries(data || []);
    }
  };

  const handleDeleteItinerary = async (id) => {
    const { error } = await supabase
      .from('itineraries')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting itinerary:', error);
    } else {
      setSavedItineraries(prev => prev.filter(it => it.id !== id));
      setDeletingId(null);
    }
  };

  const handleDuplicateItinerary = async (itinerary) => {
    // The dashboard list only holds a narrow projection (see fetchSavedItineraries) —
    // fetch the full row before duplicating so nothing gets silently dropped.
    const { data: full, error: fetchError } = await supabase
      .from('itineraries')
      .select('*')
      .eq('id', itinerary.id)
      .single();

    if (fetchError || !full) {
      console.error('Error fetching itinerary to duplicate:', fetchError);
      alert('Failed to duplicate itinerary');
      return;
    }

    const { data: newItinerary, error } = await supabase
      .from('itineraries')
      .insert({
        ...full,
        id: undefined,
        title: `${full.title} (Copy)`,
        created_at: undefined,
        updated_at: undefined
      })
      .select()
      .single();

    if (error) {
      console.error('Error duplicating itinerary:', error);
      alert('Failed to duplicate itinerary');
    } else {
      setSavedItineraries([newItinerary, ...savedItineraries]);
    }
  };

  const handleUpdateImage = async (itineraryId, imageUrl) => {
    const { error } = await supabase
      .from('itineraries')
      .update({ thumbnail_url: imageUrl })
      .eq('id', itineraryId);

    if (error) {
      console.error('Error updating image:', error);
    } else {
      setSavedItineraries(prev =>
        prev.map(it => it.id === itineraryId ? { ...it, thumbnail_url: imageUrl } : it)
      );
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400 mx-auto mb-4"></div>
            <p className="text-platinum-600">Loading...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-7xl mx-auto">
        {/* Page header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-black text-charcoal-500">My Trips</h1>
            <p className="text-charcoal-400 mt-1 text-sm">Plan, build, and revisit your travels.</p>
          </div>
          <Link to="/designer/create">
            <button className="flex items-center gap-2 px-5 py-3 bg-coral-400 hover:bg-coral-500 text-white font-bold rounded-xl transition-colors text-sm">
              <Plus size={16} />
              New trip
            </button>
          </Link>
        </div>

        {/* Trips grid */}
        <section className="mb-14">

          {savedItineraries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl bg-coral-50 flex items-center justify-center mb-5">
                <Plane size={28} className="text-coral-400" />
              </div>
              <h3 className="text-xl font-black text-charcoal-500 mb-2">No trips yet</h3>
              <p className="text-charcoal-400 mb-8 max-w-xs text-sm leading-relaxed">
                Create your first trip and start building your perfect itinerary.
              </p>
              <Link to="/designer/create">
                <button className="flex items-center gap-2 px-5 py-3 bg-coral-400 hover:bg-coral-500 text-white font-bold rounded-xl transition-colors text-sm">
                  <Plus size={16} />
                  Create your first trip
                </button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedItineraries.map((itinerary, idx) => (
                <div key={itinerary.id} className="group relative rounded-2xl overflow-hidden bg-white border border-platinum-100 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 flex flex-col">

                  {/* Cover image / gradient placeholder */}
                  <Link to={`/designer/planner/${itinerary.id}`} className="relative block h-40 overflow-hidden flex-shrink-0">
                    {itinerary.thumbnail_url ? (
                      <img
                        src={itinerary.thumbnail_url}
                        alt={itinerary.destination}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className={`w-full h-full bg-gradient-to-br ${CARD_GRADIENTS[idx % CARD_GRADIENTS.length]} flex items-center justify-center`}>
                        <Plane size={32} className="text-white/25" />
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                      <span className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-white text-charcoal-500 shadow-elevated">
                        <Edit size={14} /> Open Trip
                      </span>
                    </div>

                    {/* Days badge */}
                    <div className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      {itinerary.trip_length}d
                    </div>
                  </Link>

                  {/* Card body */}
                  <div className="p-4 flex flex-col gap-3 flex-1">

                    {/* Destination */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <MapPin size={13} className="text-coral-400 flex-shrink-0" />
                        <span className="text-sm font-bold text-charcoal-500 truncate">{itinerary.destination || 'Destination TBD'}</span>
                      </div>
                      {itinerary.title && itinerary.title !== itinerary.destination && (
                        <p className="text-xs text-charcoal-400 truncate pl-4">{itinerary.title}</p>
                      )}
                    </div>

                    {/* Footer row */}
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-platinum-50">
                      <button
                        onClick={() => setEditingImage(itinerary)}
                        className="text-xs text-platinum-400 hover:text-coral-400 transition-colors flex items-center gap-1"
                      >
                        <Image size={11} /> {itinerary.thumbnail_url ? 'Change photo' : 'Add photo'}
                      </button>

                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleDuplicateItinerary(itinerary)}
                          className="p-1.5 rounded-lg text-platinum-400 hover:text-charcoal-500 hover:bg-platinum-50 transition-colors"
                          title="Duplicate"
                        >
                          <Copy size={13} />
                        </button>
                        {deletingId === itinerary.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDeleteItinerary(itinerary.id)}
                              className="px-2 py-1 rounded-lg text-xs font-bold text-white bg-red-400 hover:bg-red-500 transition-colors"
                            >
                              Delete
                            </button>
                            <button
                              onClick={() => setDeletingId(null)}
                              className="p-1.5 rounded-lg text-platinum-400 hover:text-charcoal-500 hover:bg-platinum-50 transition-colors"
                            >
                              <X size={13} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeletingId(itinerary.id)}
                            className="p-1.5 rounded-lg text-platinum-400 hover:text-red-400 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* Image Modal */}
      {editingImage && (
        <ImageModal
          itinerary={editingImage}
          onClose={() => setEditingImage(null)}
          onSave={handleUpdateImage}
        />
      )}
    </PageContainer>
  );
}

export default TravelDesignerDashboard;
